import React, { useEffect, useState } from "react";
import { Button, Card, Col, DatePicker, Grid, Row, Table } from "antd";
import { PageHeader, StatCards } from "components/shared-components/purchase";
import AttentionPanel from "./AttentionPanel";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import Flex from "components/shared-components/Flex";
import AvatarStatus from "components/shared-components/AvatarStatus";
import ChartWidget from "components/shared-components/ChartWidget";
import { COLORS } from "constants/ChartConstant";
import utils from "utils";
import { useSelector } from "react-redux";
import { getSalesDashboardData } from "../../../../functions/reports";
import { formatPrice } from "../../../../functions/formatters";
import { CURRENCY } from "../../../../constants/DeliveryConstants";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import i18n from "i18next";
import locale from "antd/es/date-picker/locale/nl_NL";
import { DATE_FORMAT_DD_MM_YYYY } from "../../../../constants/DateConstant";
import { getOrdersInPeriod } from "../../../../functions/firestore/orders";
import * as dayjs from "dayjs";

const { RangePicker } = DatePicker;

const RevenueChart = ({ data }) => {
  const { t } = useTranslation();
  const [revenueToday, setRevenueToday] = useState(0);
  useEffect(() => {
    if (!!data && !!data.totalToday) setRevenueToday(data.totalToday);
  }, [data]);

  if (!data) return null;
  return (
    <Card
      style={{ height: "100%" }}
      title={<span style={{ fontSize: 15, fontWeight: 500 }}>{t("revenue")}</span>}
      extra={
        <span style={{ fontSize: 12.5, color: "rgba(0, 0, 0, 0.45)" }}>
          {t("today")}: <strong style={{ color: "rgba(0, 0, 0, 0.85)" }}>{CURRENCY + formatPrice(revenueToday)}</strong>
        </span>
      }
    >
      <ChartWidget
        card={false}
        series={data.dataPoints}
        xAxis={data.labels}
        height={280}
        type="bar"
        customOptions={{ colors: COLORS }}
      />
    </Card>
  );
};

const DashboardStats = ({ facts, revenue }) => {
  const { t } = useTranslation();
  if (!facts) return null;

  // Procentuele verandering t.o.v. de vorige periode; bij een custom datumbereik
  // is er geen vergelijkbare vorige periode, dus dan tonen we hem niet.
  const change =
    revenue && revenue.totalPrevPeriod
      ? Math.round(((revenue.total - revenue.totalPrevPeriod) / revenue.totalPrevPeriod) * 100 * 100) / 100
      : null;
  const changeHint =
    change !== null && !revenue.customDates
      ? `${change > 0 ? "+" : ""}${change}% t.o.v. vorige periode`
      : revenue?.readableDateRange;

  return (
    <StatCards
      items={[
        {
          label: t("revenue"),
          value: CURRENCY + formatPrice(revenue?.total ?? facts.totalRevenue),
          hint: changeHint,
          tone: change === null || revenue?.customDates ? "default" : change >= 0 ? "success" : "danger",
        },
        { label: t("orders"), value: facts.totalOrders, hint: revenue?.readableDateRange },
        {
          label: t("average_order_value"),
          value: CURRENCY + formatPrice(facts.totalOrders ? facts.totalRevenue / facts.totalOrders : 0),
        },
        {
          label: t("biggest_order"),
          value: CURRENCY + formatPrice(facts.bestOrder ? facts.bestOrder.totalPrice : 0),
        },
      ]}
    />
  );
};

const TopProduct = ({ data }) => {
  const { t } = useTranslation();
  const sales = data.sales.slice(0, 5);
  return (
    <StyledCard title={t("bestsellers")}>
      <span className="text-muted">{data.readableDateRange}</span>
      {sales.map((item) => (
        <Flex className="w-100 py-3" justifyContent="between" alignItems="center" key={item.name}>
          <AvatarStatus shape="square" src={item.img} name={item.name} subTitle={item.category} />
          <Flex>
            <div className="mr-3 text-right">
              <span className="text-muted">{t("revenue")}</span>
              <div className="mb-0 h5 font-weight-bold">{CURRENCY + formatPrice(item.totalRevenue)}</div>
            </div>
          </Flex>
        </Flex>
      ))}
    </StyledCard>
  );
};

const tableColumns = [
  {
    title: i18n.t("order.order_id"),
    dataIndex: "orderId",
  },
  {
    title: i18n.t("name"),
    dataIndex: "name",
    render: (_, record) => record.customer.firstName + " " + record.customer.lastName,
  },
  {
    title: i18n.t("date"),
    dataIndex: "date",
    render: (_, record) => <span>{dayjs.unix(record.createdAt).format("D MMM")}</span>,
  },
  {
    title: i18n.t("total"),
    dataIndex: "amount",
    render: (_, record) => (
      <span className="font-weight-semibold text-nowrap">{CURRENCY + formatPrice(record.totalPrice)}</span>
    ),
    sorter: (a, b) => utils.antdTableSorter(a, b, "amount"),
  },
];

const BestOrder = ({ data }) => {
  const { t } = useTranslation();
  return (
    <StyledCard title={t("biggest_sales")}>
      <span className="text-muted">{data.readableDateRange}</span>
      <Table
        pagination={false}
        columns={tableColumns}
        dataSource={data.bestOrders}
        rowKey="id"
        scroll={{ x: 100 }}
        style={{ marginTop: 12 }}
      />
    </StyledCard>
  );
};

const SalesDashboard = () => {
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const orders = useSelector((state) => state.firestoreReducer.ordered.orders);
  const categories = useSelector((state) => state.firestoreReducer.ordered.categories);
  const [salesData, setSalesData] = useState(getSalesDashboardData({ orders, categories }));
  const [currentWeekOffset, setCurrentWeekOffset] = useState(0);

  const onChangeRange = (value) => {
    if (!!value && value.length === 2) {
      const start = dayjs(value[0]).startOf("day").unix();
      const end = dayjs(value[1]).endOf("day").unix();
      getOrdersInPeriod(start, end).then((rangeOrders) => {
        setSalesData(getSalesDashboardData({ orders: rangeOrders, categories, start, end }));
      });
    }
  };

  const navigateWeek = (direction) => {
    const newOffset = currentWeekOffset + direction;
    setCurrentWeekOffset(newOffset);
    const start = dayjs().add(newOffset, "week").startOf("week").unix();
    const end = dayjs().add(newOffset, "week").endOf("week").unix();
    getOrdersInPeriod(start, end).then((rangeOrders) => {
      setSalesData(getSalesDashboardData({ orders: rangeOrders, categories, start, end }));
    });
  };

  const goToCurrentWeek = () => {
    setCurrentWeekOffset(0);
    const start = dayjs().startOf("week").unix();
    const end = dayjs().endOf("week").unix();
    getOrdersInPeriod(start, end).then((rangeOrders) => {
      setSalesData(getSalesDashboardData({ orders: rangeOrders, categories, start, end }));
    });
  };

  const getCurrentWeekNumber = () => {
    return dayjs().add(currentWeekOffset, "week").week();
  };

  return (
    <Wrapper>
      <PageHeader
        title={t("reports")}
        subtitle={salesData?.revenue?.readableDateRange}
        extra={
          <>
            <WeekNavigator>
              <Button icon={<LeftOutlined />} onClick={() => navigateWeek(-1)} />
              <Button onClick={goToCurrentWeek} style={{ minWidth: isMobile ? undefined : 140, flex: isMobile ? 1 : undefined }}>
                {currentWeekOffset === 0 ? "Deze week" : `Week ${getCurrentWeekNumber()}`}
              </Button>
              <Button icon={<RightOutlined />} onClick={() => navigateWeek(1)} />
            </WeekNavigator>
            {!isMobile && <RangePicker onChange={onChangeRange} locale={locale} format={DATE_FORMAT_DD_MM_YYYY} />}
          </>
        }
      />

      <DashboardStats facts={salesData.facts} revenue={salesData.revenue} />

      {/* Grafiek links, openstaande punten rechts: de cijfers vertellen hoe het
          ging, het paneel ernaast wat er nog moet gebeuren. */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={15} xl={16}>
          <RevenueChart data={salesData.revenue} />
        </Col>
        <Col xs={24} lg={9} xl={8}>
          <AttentionPanel />
        </Col>
      </Row>
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={12}>
          <TopProduct data={salesData.productSales} />
        </Col>
        <Col xs={24} lg={12}>
          <BestOrder data={salesData.bestOrders} />
        </Col>
      </Row>
    </Wrapper>
  );
};

const StyledCard = styled(Card)`
  padding-top: 12px;
`;

const WeekNavigator = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const Wrapper = styled.div`
  @media (max-width: 768px) {
    padding: 8px;
  }
`;

export default SalesDashboard;
